import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Eye, Activity, ArrowLeft } from "lucide-react";
import CircleGlow from "../components/CircleGlow";
import { leeuwardenSystems } from "../data/leeuwardenSystems";
import { loadAssessments } from "../lib/assessmentStore";

/**
 * /publiek-overzicht — THE CITIZEN VIEW.
 *
 * No admin chrome.  No delete actions.  No technical readiness gauges.
 * Just the narratives, the "Veilig" status, and friendly information for
 * residents who want to know what algorithms their gemeente uses.
 */

const FORMAT_DATE = (iso) =>
  new Date(iso).toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const LWD_LAST_TESTED = {
  uitkeringscheck: "2026-01-04",
  schuldhulpverlening: "2025-12-18",
  "wmo-zorg": "2025-11-30",
  handhaving: "2026-01-02",
};

const Public = () => {
  const [assessments, setAssessments] = useState([]);

  useEffect(() => {
    setAssessments(loadAssessments());
  }, []);

  const publishedUser = useMemo(
    () => assessments.filter((a) => a.published),
    [assessments]
  );

  const cards = useMemo(() => {
    const lwd = leeuwardenSystems.map((s) => ({
      kind: "lwd",
      id: s.id,
      code: s.code,
      name: s.name,
      tagline: s.tagline,
      icon: s.icon,
      narrative: s.narrative,
      publicValue: s.publicValue,
      safeguards: s.safeguards,
      lastTested: LWD_LAST_TESTED[s.id] || "2026-01-01",
    }));
    const usr = publishedUser.map((u) => ({
      kind: "user",
      id: u.id,
      code: u.code,
      name: u.name,
      tagline: u.domain,
      icon: null,
      narrative: u.narrative,
      publicValue:
        "Dit algoritme is door " +
        u.org +
        " in gebruik genomen na een afgeronde compliance-toets.",
      safeguards: u.safeguardLabels || [],
      lastTested: u.publishedAt || u.createdAt,
    }));
    return [...lwd, ...usr];
  }, [publishedUser]);

  return (
    <div data-testid="page-public">
      {/* HEADER */}
      <section className="relative overflow-hidden">
        <CircleGlow intensity={0.65} />
        <div className="rk-container relative pt-16 md:pt-24 pb-14">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-10">
            <div className="flex-1 min-w-0">
              <div
                className="overline mb-6 flex items-center gap-3"
                data-testid="public-eyebrow"
              >
                <span
                  className="inline-block w-2 h-2 rounded-full bg-[#9FE2BF]"
                  style={{
                    boxShadow: "0 0 8px rgba(159,226,191,0.7)",
                    animation: "rk-pulse 2.4s ease-in-out infinite",
                  }}
                />
                Live · Publiek overzicht voor inwoners
              </div>

              <h1
                className="text-white tracking-tight leading-[1.05] max-w-4xl text-5xl md:text-7xl"
                style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
                data-testid="public-title"
              >
                De algoritmes van uw gemeente,{" "}
                <em
                  className="italic text-[#D4D8DF]"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  in begrijpelijke taal.
                </em>
              </h1>

              <p
                className="mt-6 text-lg text-[#D4D8DF] max-w-3xl leading-relaxed"
                data-testid="public-intro"
              >
                Hier ziet u welke geautomatiseerde systemen Gemeente Leeuwarden
                gebruikt, wat ze doen, en welke waarborgen er zijn. Alle{" "}
                <span className="text-white">{cards.length} systemen</span>{" "}
                staan op dit moment{" "}
                <span className="text-white">veilig</span> onder continu
                toezicht van RiskaiΛ.
              </p>
            </div>

            {/* Leeuwarden municipality crest — anchors the partnership */}
            <div
              className="shrink-0 flex flex-col items-center gap-3 self-start md:self-end md:pt-2"
              data-testid="public-leeuwarden-crest"
            >
              <img
                src="https://customer-assets.emergentagent.com/wingman/368cca20-abd0-4588-99d9-9e42b06e9661/attachments/d32f935dacb74e34a929d8d2c7f15683_image.png"
                alt="Gemeente Leeuwarden"
                draggable={false}
                style={{
                  height: "64px",
                  width: "64px",
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: "1px solid rgba(192,197,206,0.30)",
                  boxShadow: "0 8px 24px -10px rgba(0,0,0,0.55)",
                }}
              />
              <div
                className="text-[10px] tracking-[0.22em] uppercase whitespace-nowrap text-center"
                style={{ color: "#9AA4B2" }}
              >
                In samenwerking met
                <br />
                <span style={{ color: "#D4D8DF" }}>Gemeente Leeuwarden</span>
              </div>
            </div>
          </div>
        </div>
        <div className="hairline" />
      </section>

      {/* STATUS STRIP */}
      <section
        className="rk-container py-12"
        data-testid="public-status-strip"
      >
        <div
          className="grid gap-px bg-[#C0C5CE]/15"
          style={{
            gridTemplateColumns: `repeat(auto-fit, minmax(220px, 1fr))`,
          }}
        >
          {cards.map((s) => (
            <div
              key={s.id}
              className="bg-[#1C3A5A] p-6 flex items-center gap-4"
              data-testid={`public-status-${s.id}`}
            >
              <ShieldCheck
                strokeWidth={1}
                className="w-6 h-6 text-[#9FE2BF] shrink-0"
              />
              <div>
                <div className="overline text-[10px]">{s.code}</div>
                <div
                  className="text-white text-base"
                  style={{ fontFamily: "Fraunces, serif", fontWeight: 400 }}
                >
                  Veilig
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CARDS — citizen narrative only */}
      <section
        className="rk-container pb-24"
        data-testid="public-systems"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#C0C5CE]/15">
          {cards.map((s) => {
            const Icon = s.icon;
            return (
              <article
                key={s.id}
                data-testid={`public-system-${s.id}`}
                className="bg-[#162E4A] p-8 md:p-10 hover:bg-[#18335A] transition-colors duration-500 relative"
              >
                <div className="flex items-start justify-between gap-6 mb-6">
                  <div>
                    <div className="overline mb-2">{s.tagline}</div>
                    <h3
                      className="text-2xl md:text-3xl text-white tracking-tight"
                      style={{ fontFamily: "Fraunces, serif", fontWeight: 400 }}
                    >
                      {s.name}
                    </h3>
                  </div>
                  {Icon ? (
                    <div
                      className="shrink-0 w-14 h-14 border border-[#C0C5CE]/40 flex items-center justify-center"
                      aria-hidden
                    >
                      <Icon
                        strokeWidth={1}
                        className="w-7 h-7 text-[#C0C5CE]"
                      />
                    </div>
                  ) : null}
                </div>

                <p
                  className="text-[#D4D8DF] leading-relaxed mb-6"
                  data-testid={`public-narrative-${s.id}`}
                >
                  {s.narrative}
                </p>

                {s.publicValue && (
                  <div
                    className="text-sm leading-relaxed mb-6"
                    style={{ color: "#9AA4B2" }}
                  >
                    <span className="overline mb-1.5 block">
                      Publieke waarde
                    </span>
                    {s.publicValue}
                  </div>
                )}

                {s.safeguards && s.safeguards.length > 0 && (
                  <>
                    <div className="hairline my-5" />
                    <div className="flex items-start gap-3">
                      <Eye
                        strokeWidth={1}
                        className="w-4 h-4 mt-1 text-[#C0C5CE] shrink-0"
                      />
                      <div>
                        <div className="overline mb-2">Wat is er beschermd?</div>
                        <ul className="space-y-1.5 text-sm text-[#D4D8DF]">
                          {s.safeguards.map((g) => (
                            <li
                              key={g}
                              className="flex items-start gap-2"
                            >
                              <span className="mt-2 w-1.5 h-1.5 bg-[#C0C5CE] rounded-full shrink-0" />
                              {g}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </>
                )}

                <div className="hairline my-5" />

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <span
                    className="text-[10px] tracking-[0.18em] uppercase text-[#9AA4B2]"
                    data-testid={`public-tested-${s.id}`}
                  >
                    Laatst getoetst {FORMAT_DATE(s.lastTested)}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase text-[#9FE2BF]">
                      <Activity strokeWidth={1.25} className="w-3.5 h-3.5" />
                      Continu onder toezicht
                    </span>
                    <span
                      className="rk-chip"
                      data-testid={`public-veilig-${s.id}`}
                      style={{
                        borderColor: "rgba(159, 226, 191, 0.55)",
                        color: "#9FE2BF",
                      }}
                    >
                      <ShieldCheck strokeWidth={1.25} className="w-3.5 h-3.5" />
                      Veilig
                    </span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Reassurance footer */}
        <p className="mt-10 text-sm text-[#9AA4B2] max-w-3xl">
          Hebt u een vraag over een algoritme of denkt u dat een besluit niet
          klopt? U heeft altijd het recht op uitleg, inzage en bezwaar — neem
          contact op met Gemeente Leeuwarden.
        </p>

        <div className="mt-8">
          <Link
            to="/contact"
            className="rk-btn-ghost"
            data-testid="public-contact-cta"
          >
            <ArrowLeft strokeWidth={1.25} className="w-4 h-4 rotate-180" />
            Stel een vraag aan de gemeente
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Public;
